import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, FilePlus2, FileSpreadsheet, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ResultCard } from "@/components/converter/ResultCard";
import { ProgressCard } from "@/components/converter/ProgressCard";
import { AddFilesDialog } from "@/components/converter/AddFilesDialog";
import { API } from "@/lib/api";
import { useJob } from "@/lib/useJob";
import { clearLastJobId, getLastJobId, setLastJobId } from "@/lib/lastJob";

const TopBar = ({ canAdd, onAdd }) => (
  <header data-testid="table-topbar" className="mb-6 flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 pb-5">
    <div className="flex items-center gap-2 text-emerald-700">
      <FileSpreadsheet className="h-5 w-5" />
      <h1 className="font-display text-lg font-bold text-slate-900">Tabella movimenti</h1>
    </div>
    <Button data-testid="add-movements-button" onClick={onAdd} disabled={!canAdd}
      className="rounded-full bg-emerald-700 text-white hover:bg-emerald-800 btn-lift">
      <FilePlus2 className="mr-2 h-4 w-4" /> Aggiungi movimenti
    </Button>
  </header>
);

export default function TablePage() {
  const { jobId } = useParams();
  const { job, error, offline, refresh } = useJob(jobId);
  const [adding, setAdding] = useState(false);
  const done = job?.status === "done";
  const processing = job?.status === "processing" && !error;
  const appending = processing && job.base_files > 0;

  useEffect(() => {
    if (job) setLastJobId(jobId);
    else if (error && getLastJobId() === jobId) clearLastJobId();
  }, [job, error, jobId]);

  return (
    <main data-testid="table-page" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <TopBar canAdd={done} onAdd={() => setAdding(true)} />
      <AddFilesDialog open={adding} onOpenChange={setAdding} jobId={jobId} onStarted={refresh} />
      {done && job.append_error && (
        <div data-testid="append-error" className="mb-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {job.append_error}
        </div>
      )}
      {done && <ResultCard key={`${job.file_count}-${job.summary.count}`} job={job} baseUrl={`${API}/jobs/${jobId}`} />}
      {processing && (
        <div className="mx-auto max-w-4xl">
          {appending && (
            <p data-testid="appending-notice" className="mb-3 text-sm text-slate-600">
              Sto aggiungendo {job.file_count - job.base_files} {job.file_count - job.base_files === 1 ? "nuovo file" : "nuovi file"} ai movimenti già convertiti: la tabella tornerà con tutte le righe insieme.
            </p>
          )}
          <ProgressCard job={job} fileName={job.file_count > 1 ? `${job.file_count} estratti conto` : job.filename}
            startedAt={job.created * 1000} offline={offline} />
        </div>
      )}
      {!job && !error && (
        <div className="flex items-center gap-2 py-16 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>
      )}
      {error && (
        <div data-testid="table-page-error" className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-slate-700">
          {error}{/non trovata|scaduta/i.test(error) && " I dati restano disponibili per circa un'ora dopo la conversione."}
          <Link to="/" className="ml-2 font-semibold text-emerald-700 underline underline-offset-4">Carica di nuovo il PDF</Link>
        </div>
      )}
    </main>
  );
}
